import axios, { AxiosResponse } from 'axios';
import type { ApiResponse } from '@/types/api.ts';
import type { GetClientBillsResponse, APIGetAppSettingsResBody, ExchangeRateConfig } from '@spona/rest-api-types';

// ==================== TYPE DEFINITIONS ====================

export interface AppSettings {
    // Define app settings structure based on what's used in the application
    [key: string]: any;
}

//TODO: Use this data structure in the backend  
export interface CompanyInfo {
    name?: string;
    address?: string;
    phone?: string;
    email?: string;
    text?: string;
    // Add other company info fields as needed
}

export interface OrderNote {
    note?: string;
    text?: string;
    // Add other order note fields as needed
}

export interface DefaultDeadline {
    to: number;
}

// ==================== SETTINGS API CLASS ====================

export class SettingsAPI {
    /**
     * Get application settings (includes nbsRateToday injected by backend)
     */
    async getAppSettings(): Promise<ApiResponse<APIGetAppSettingsResBody>> {
        try {
            const response: AxiosResponse<APIGetAppSettingsResBody> = await axios.get('/api/setting/app-settings');
            return {
                success: true,
                data: response.data
            };
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to fetch app settings'
            };
        }
    }

    /**
     * Update application settings
     */
    async updateAppSettings(settings: Partial<AppSettings>): Promise<ApiResponse<void>> {
        try {
            await axios.post('/api/setting/app-settings', settings);
            return {
                success: true
            };
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to update app settings'
            };
        }
    }

    /**
     * Get company information
     */
    async getCompanyInfo(): Promise<ApiResponse<CompanyInfo>> {
        try {
            const response: AxiosResponse<CompanyInfo> = await axios.get('/api/setting/company-info');
            return {
                success: true,
                data: response.data
            };
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to fetch company info'
            };
        }
    }

    /**
     * Update company information
     */
    async updateCompanyInfo(companyInfo: Partial<CompanyInfo>): Promise<ApiResponse<void>> {
        try {
            await axios.post('/api/setting/company-info', companyInfo);
            return {
                success: true
            };
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to update company info'
            };
        }
    }

    /**
     * Get order note
     */
    async getOrderNote(): Promise<ApiResponse<OrderNote>> {
        try {
            const response: AxiosResponse<OrderNote> = await axios.get('/api/setting/order-note');
            return {
                success: true,
                data: response.data
            };
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to fetch order note'
            };
        }
    }

    /**
     * Update order note
     */
    async updateOrderNote(orderNote: Partial<OrderNote>): Promise<ApiResponse<void>> {
        try {
            await axios.post('/api/setting/order-note', orderNote);
            return {
                success: true
            };
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to update order note'
            };
        }
    }

    /**
     * Get default deadline settings
     */
    async getDefaultDeadline(): Promise<ApiResponse<DefaultDeadline>> {
        try {
            const response: AxiosResponse<DefaultDeadline> = await axios.get('/api/setting/default-deadline');
            return {
                success: true,
                data: response.data
            };
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to fetch default deadline'
            };
        }
    }

    /**
     * Update default deadline settings
     */
    async updateDefaultDeadline(payload: { to: number }): Promise<ApiResponse<void>> {
        try {
            await axios.post('/api/setting/default-deadline', payload);
            return {
                success: true
            };
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to update default deadline'
            };
        }
    }

    /**
     * Change password
     */
    async changePassword(payload: { old: string; new: string }): Promise<ApiResponse<void>> {
        try {
            await axios.post('/api/setting/change-password', payload);
            return {
                success: true
            };
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to change password'
            };
        }
    }

    /**
     * Set custom exchange rate for a currency, or clear override (customRate null/undefined).
     */
    async setExchangeRate(currency: string, customRate: number | null): Promise<ApiResponse<void>> {
        try {
            await axios.post('/api/setting/exchange-rates', { currency, customRate });
            return { success: true };
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to update exchange rate'
            };
        }
    }

    /**
     * Get bill history for the authenticated admin's client.
     */
    async getClientBills(params?: {
        page?: number;
        limit?: number;
        isPaid?: boolean;
    }): Promise<ApiResponse<GetClientBillsResponse>> {
        try {
            const query: Record<string, string> = {};
            if (params?.page !== undefined) query.page = String(params.page);
            if (params?.limit !== undefined) query.limit = String(params.limit);
            if (params?.isPaid !== undefined) query.isPaid = String(params.isPaid);

            const response: AxiosResponse<GetClientBillsResponse> = await axios.get(
                '/api/setting/billing/bills',
                { params: query }
            );
            return { success: true, data: response.data };
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to fetch billing history'
            };
        }
    }
}

// ==================== EXPORTS ====================

// Create singleton instance
export const settingsApi = new SettingsAPI();

// Default export for convenience
export default settingsApi;